<template>
  <div class="drawer-overlay" v-if="visible" @click="handleOverlayClick">
    <div class="drawer-content" @click.stop>
      <!-- 标题区域 -->
      <div class="drawer-header">
        <div class="header-content">
          <h3 class="title-main">{{ $t('customSolutionDrawer.titleMain') }}</h3>
          <p class="title-sub">{{ $t('customSolutionDrawer.titleSub') }}</p>
        </div>
        <button class="close-btn" @click="closeDrawer">×</button>
      </div>
      
      <!-- 内容区域 -->
      <div class="drawer-body">
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>{{ $t('common.loading') }}</p>
        </div>
        
        <div v-else class="form-content">
          <!-- 单选区域 -->
          <div class="selection-section" v-if="singleOptions.length > 0">
            <h4 class="section-title">{{ $t('customSolutionDrawer.singleSectionTitle') }}</h4>
            <div class="options-grid">
              <div 
                v-for="option in singleOptions" 
                :key="option.id"
                class="option-item"
                :class="{ 'selected': selectedSingle === option.id }"
                @click="handleSingleSelect(option.id)"
              >
                <span>{{ option.label }}</span>
              </div>
            </div>
          </div>
          
          <!-- 多选区域 -->
          <div class="selection-section" v-if="multiOptions.length > 0">
            <h4 class="section-title">{{ $t('customSolutionDrawer.multiSectionTitle') }}</h4>
            <div class="multi-options-wrap">
              <div 
                v-for="option in multiOptions" 
                :key="option.id"
                class="multi-option-item"
                :class="{ 'selected': selectedMulti.includes(option.id) }"
                @click="handleMultiSelect(option.id)"
              >
                <span>{{ option.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 底部按钮 -->
      <div class="drawer-footer">
        <button class="submit-btn" @click="handleSubmit" :disabled="submitting || loading">
          {{ submitting ? $t('customSolutionDrawer.submitting') : $t('customSolutionDrawer.submit') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getQuestionnaire } from '@/net/api';

export default {
  name: 'CustomSolutionDrawer',
  
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      formData: {},
      formFields: [],
      loading: false,
      submitting: false,
      // 单选数据
      singleOptions: [],
      selectedSingle: null,
      // 多选数据
      multiOptions: [],
      selectedMulti: [],
      // 存储key与id的映射
      optionKeyMap: {} // { id: key }
    }
  },
  
  watch: {
    visible(val) {
      if (val) {
        this.fetchFormFields();
      } else {
        setTimeout(() => {
          this.resetForm();
        }, 300);
      }
    }
  },
  
  methods: {
    handleOverlayClick() {
      this.closeDrawer();
    },
    
    closeDrawer() {
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    
    // 处理多选选择
    handleMultiSelect(id) {
      const index = this.selectedMulti.indexOf(id);
      if (index > -1) {
        this.selectedMulti.splice(index, 1);
      } else {
        this.selectedMulti.push(id);
      }
      console.log('选中多选项:', this.selectedMulti);
    },
    
    // 处理单选选择
    handleSingleSelect(id) {
      // 如果点击的是已选中的项，取消选中
      if (this.selectedSingle === id) {
        this.selectedSingle = null;
        console.log('取消选中单选项');
      } else {
        // 否则选中新的项
        this.selectedSingle = id;
        console.log('选中单选项:', id);
      }
    },
    
    // 获取表单字段配置
    async fetchFormFields() {
      this.loading = true;
      try {
        const response = await getQuestionnaire();
        
        if (response && Array.isArray(response)) {
          let singleId = 1;
          let multiId = 1;
          
          response.forEach(item => {
            const option = {
              id: item.type === 'single' ? singleId++ : multiId++,
              label: item.question,
              key: item.key
            };
            
            // 存储key映射
            this.optionKeyMap[option.id] = item.key;
            
            if (item.type === 'single') {
              this.singleOptions.push(option);
            } else if (item.type === 'multiple') {
              this.multiOptions.push(option);
            }
          });
        }
      } catch (error) {
        console.error('获取表单配置失败:', error);
        this.showMessage(this.$t('customSolutionDrawer.loadFailed'));
      } finally {
        this.loading = false;
      }
    },
    
    validateForm() {
      // 暂时不做必填验证，因为都是非必填
      return true;
    },
    
    async handleSubmit() {
      if (!this.validateForm()) return;
      
      this.submitting = true;
      
      try {
        // 收集选中的key
        const selectedKeys = [];
        
        // 添加单选key
        if (this.selectedSingle) {
          const singleKey = this.optionKeyMap[this.selectedSingle];
          if (singleKey) {
            selectedKeys.push(singleKey);
          }
        }
        
        // 添加多选keys
        this.selectedMulti.forEach(id => {
          const multiKey = this.optionKeyMap[id];
          if (multiKey) {
            selectedKeys.push(multiKey);
          }
        });
        
        const submitData = {
          selectedKeys: selectedKeys,
          singleSelect: this.selectedSingle,
          multiSelect: this.selectedMulti
        };
        
        console.log('提交的keys:', selectedKeys);
        
        // 触发提交事件
        this.$emit('submit', submitData);
      } catch (error) {
        console.error('提交失败:', error);
        this.showMessage(this.$t('customSolutionDrawer.submitFailed'));
      } finally {
        this.submitting = false;
      }
    },
    
    showMessage(message) {
      if (this.$toast) {
        this.$toast(message);
      } else {
        alert(message);
      }
    },
    
    resetForm() {
      this.formData = {};
      this.formFields = [];
      this.selectedSingle = null;
      this.selectedMulti = [];
      this.singleOptions = [];
      this.multiOptions = [];
      this.optionKeyMap = {};
    }
  }
}
</script>

<style scoped lang="scss">
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.drawer-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: #ffffff;
  border-radius: 20px 20px 0 0;
  max-height: 85vh;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 30px;
  border-bottom: 2px solid #F1F5F9;
  
  .header-content {
    flex: 1;
    
    .title-main {
      font-size: 34px;
      font-weight: 500;
      color: #003CBF;
      margin: 0 0 10px 0;
      line-height: 1.3;
    }
    
    .title-sub {
      font-size: 22px;
      font-weight: normal;
      color: #6A7282;
      margin: 0;
      line-height: 1.5;
    }
  }
  
  .close-btn {
    width: 50px;
    height: 50px;
    border: none;
    background: transparent;
    font-size: 40px;
    color: #999;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    flex-shrink: 0;
    margin-left: 15px;
    
    &:active {
      color: #666;
    }
  }
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 30px;
  
  .form-content {
    .selection-section {
      margin-bottom: 50px;
      
      &:last-child {
        margin-bottom: 40px;
      }
      
      .section-title {
        font-size: 28px;
        color: #0A0A0A;
        font-weight: 600;
        margin: 0 0 20px 0;
      }
      
      .options-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 20px 25px;
        
        .option-item {
          background: #FFFFFF;
          border: 1.71px solid #E5E7EB;
          border-radius: 16px;
          padding: 25px 20px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          
          span {
            font-size: 26px;
            color: #333;
          }
          
          &.selected {
            background: #EFF6FF;
            border: 1.71px solid #003CBF;
            
            span {
              color: #003CBF;
            }
          }
          
          &:active {
            opacity: 0.8;
          }
        }
      }
      
      .multi-options-wrap {
        display: flex;
        flex-wrap: wrap;
        gap: 22px 15px;
        
        .multi-option-item {
          background: #F1F5F9;
          border: 1.71px solid transparent;
          border-radius: 999px;
          padding: 15px 30px;
          cursor: pointer;
          transition: all 0.3s ease;
          
          span {
            font-size: 26px;
            color: #333;
            white-space: nowrap;
          }
          
          &.selected {
            background: #EFF6FF;
            border: 1.71px solid #003CBF;
            
            span {
              color: #003CBF;
            }
          }
          
          &:active {
            opacity: 0.8;
          }
        }
      }
    }
  }
  
  .loading-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
    color: #999;
    
    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid #f3f3f3;
      border-top: 3px solid #003CBF;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 20px;
    }
    
    p {
      font-size: 26px;
      margin: 0;
    }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.form-item {
  margin-bottom: 30px;
  
  label {
    display: block;
    font-size: 28px;
    color: #333;
    margin-bottom: 15px;
    font-weight: 500;
    
    .required {
      color: #ff4d4f;
      margin-left: 4px;
    }
  }
  
  input, textarea {
    width: 100%;
    padding: 20px;
    font-size: 28px;
    border: 1px solid #d9d9d9;
    border-radius: 10px;
    box-sizing: border-box;
    color: #333;
    
    &::placeholder {
      color: #bfbfbf;
    }
    
    &:focus {
      outline: none;
      border-color: #003cbf;
    }
  }
  
  textarea {
    min-height: 120px;
    resize: vertical;
    font-family: inherit;
  }
}

.drawer-footer {
  padding: 20px 30px 40px;
  border-top: 1px solid #f0f0f0;
  
  .submit-btn {
    width: 100%;
    height: 90px;
    background: #003cbf;
    color: #ffffff;
    border: none;
    border-radius: 45px;
    font-size: 30px;
    font-weight: 500;
    cursor: pointer;
    
    &:active:not(:disabled) {
      background: #0056d3;
    }
    
    &:disabled {
      background: #d9d9d9;
      cursor: not-allowed;
    }
  }
}
</style>
